import AxiosApiService from "./AxiosApiService";
import { askToRetry, isRetryableError } from "../utils/networkErrorBus";
const domain_url = window.location.origin;
const API_URL = domain_url + "/admin";
const DOCUMENT_STORE_URL = domain_url + "/document-store";
const APPLICATION_URL = domain_url + "/framework";
const GRAPH_STORE_URL = domain_url + "/nl2graph";
const HISTORY_URL = domain_url + "/solution-history";
const AUTO_QA_URL = domain_url + "/auto-qa";
const knowledgeGraph_URL = domain_url + "/knowledge-graph";
const CONSISTENCY_GUARD_URL = domain_url + "/consistency-guard";
const AI_SERVER = domain_url + "/onboarding-assistant"
const services = new AxiosApiService();

const authHeaders = (token: string, extra: Record<string, string> = {}) => ({
  Authorization: `Bearer ${token}`,
  'conversation-id': '1',
  ...extra,
});

// Runs a raw fetch call, and on a network failure or server error, offers the user a retry.
const fetchWithRetry = async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
  while (true) {
    try {
      const response = await fetch(input, init);
      if (!response.ok && response.status >= 500 && (await askToRetry())) {
        continue;
      }
      return response;
    } catch (error) {
      if (isRetryableError(error) && (await askToRetry())) {
        continue;
      }
      throw error;
    }
  }
};

export const getChatHistoryApi = async <T>(
  userId: string,
  token: string
): Promise<T | null> => {
  const url = `${API_URL}/getConversationThreads?user_id=${userId}`;
  return services.getAsync<T>(url, { headers: authHeaders(token) });
};

export const getConversationThreadsByThreadIdApi = async <T>(
  threadID: string,
  token: string
): Promise<T | null> => {
  const url = `${API_URL}/getConversationThreadsByThreadId?threadID=${threadID}`;
  try {
    return await services.getAsync<T>(url, { headers: authHeaders(token) });
  } catch (error) {
    console.error("Error fetching conversation threads:", error);
    return null;
  }
};

export const getDashboardDataApi = async <T>(
  bluePrintName: string,
  token: string
): Promise<T | null> => {
  const url = `${API_URL}/dashboard/${bluePrintName}`;
  return services.getAsync<T>(url, { headers: authHeaders(token) });
};

class ChatApiService {
  async sendChatMessage<T>(payload: any, token: string): Promise<T | null> {
    return services.postAsync<T>(`${APPLICATION_URL}/receive_from_chat_service`, payload, { headers: authHeaders(token) });
  }

  async sendChatResponseBackByData<T>(payload: any, token: string): Promise<T | null> {
    return services.postAsync<T>(`${APPLICATION_URL}/chat_response_back_by_data`, payload, {
      headers: {
        ...authHeaders(token),
        "Content-Type": "application/json",
      },
    });
  }

  async sendChatResponseBackByFilename<T>(payload: any, token: string): Promise<T | null> {
    return services.postAsync<T>(`${APPLICATION_URL}/chat_response_back_by_filename`, payload, {
      headers: {
        ...authHeaders(token),
        "Content-Type": "application/json",
      },
    });
  }

  async saveFeedbackResponse<T>(messageId: string, payload: any, token: string): Promise<T | null> {
    return services.postAsync<T>(`${APPLICATION_URL}/save_feedback_response/${messageId}`, payload, {
      headers: {
        ...authHeaders(token),
        "Content-Type": "application/json",
      },
    });
  }
}

export const getNlGraphData = async <T>(
  search_query: string,
  token: string
): Promise<T | null> => {
  const url = `${DOCUMENT_STORE_URL}/get_documents?search_properties=${encodeURIComponent(search_query)}`;
  return services.getAsync<T>(url, { headers: authHeaders(token) });
};

export const uploadFile = async <T>(
  payload: any,
  token: string
): Promise<T | null> => {
  return services.postAsync<T>(`${DOCUMENT_STORE_URL}/upload_file`, payload, { headers: authHeaders(token) });
};

export const readSelectedFile = async (
  fileName: string,
  token: string
): Promise<Blob> => {
  const url = `${DOCUMENT_STORE_URL}/get_file_bytes?file_name=${encodeURIComponent(fileName)}`;
  const response = await fetchWithRetry(url, { method: 'GET', headers: authHeaders(token) });

  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.statusText}`);
  }
  return response.blob();
};

export const getGraphData = async <T>(
  payload: any,
  token: string
): Promise<T | null> => {
  return services.postAsync<T>(`${GRAPH_STORE_URL}/generate_graph`, payload, { headers: authHeaders(token) });
};

export const getHistoryApi = async <T>(
  token: string
): Promise<T | null> => {
  return services.getAsync<T>(`${HISTORY_URL}/get_history`, { headers: authHeaders(token) });
};

export const deleteHistoryApi = async <T>(
  item_id: number,
  token: string
): Promise<T | null> => {
  const url = `${HISTORY_URL}/delete_item/${encodeURIComponent(item_id)}`;
  return services.deleteAsync<T>(url, { headers: authHeaders(token) });
};

export const downloadHistoryFile = async (
  item_id: number,
  token: string
): Promise<Response> => {
  const url = `${HISTORY_URL}/download/${encodeURIComponent(item_id)}`;
  const response = await fetchWithRetry(url, { method: 'GET', headers: authHeaders(token) });

  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.statusText}`);
  }
  return response;
};

export const autoQaFillForm = async <T>(
  payload: any,
  token: string
): Promise<T | null> => {
  return services.postAsync<T>(`${AUTO_QA_URL}/fill_form`, payload, { headers: authHeaders(token) });
};

export const getProjectInfoApi = async <T>(
  project_name: string,
  token: string
): Promise<T | null> => {
  const url = `${AUTO_QA_URL}/get_project_info?project_name=${encodeURIComponent(project_name)}`;
  return services.getAsync<T>(url, { headers: authHeaders(token) });
};

export const getGenQADashboardApi = async <T>(
  token: string
): Promise<T | null> => {
  return services.getAsync<T>(`${AUTO_QA_URL}/GenQ&A/dashboard`, { headers: authHeaders(token) });
};

export const approveAnswerApi = async <T>(
  payload: any,
  token: string
): Promise<T | null> => {
  return services.postAsync<T>(`${AUTO_QA_URL}/approve_answer`, payload, { headers: authHeaders(token) });
};

export const downloadExcelApi = async <T>(
  project_name: string,
  token: string
): Promise<T | null> => {
  const url = `${AUTO_QA_URL}/download_excel?project_name=${encodeURIComponent(project_name)}`;
  return services.postAsync<T>(url, {}, { headers: authHeaders(token) });
};

export const manualAnswerApi = async <T>(
  payload: any,
  token: string
): Promise<T | null> => {
  return services.postAsync<T>(`${AUTO_QA_URL}/manual_answer`, payload, { headers: authHeaders(token) });
};

export const qaResetApi = async <T>(
  payload: any,
  token: string
): Promise<T | null> => {
  return services.postAsync<T>(`${AUTO_QA_URL}/reset`, payload, { headers: authHeaders(token) });
};

export async function knowledgeGraphChat(payload: any, token: string) {
  try {
    const response = await fetchWithRetry(`${knowledgeGraph_URL}/chat_response`, {
      method: "POST",
      headers: {
        ...authHeaders(token),
        "Content-Type": "application/json",
      },
      body: payload,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch KGChat: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    return { success: false, message: "Failed to fetch KGChat response" };
  }
}

export const getDocumentsMetadata = async <T>(
  filenames: string[],
  token: string
): Promise<T | null> => {
  const queryParams = filenames.map(f => `filenames=${encodeURIComponent(f)}`).join('&');
  const url = `${DOCUMENT_STORE_URL}/get_summary_filenames?${queryParams}`;
  return services.getAsync<T>(url, { headers: authHeaders(token) });
};

export const getDocumentLinksByFilename = async <T>(
  fileName: string,
  userQuery: string,
  depth: number,
  token: string
): Promise<T | null> => {
  const url = `${DOCUMENT_STORE_URL}/get_document_links_by_filename?file_name=${encodeURIComponent(fileName)}&user_query=${encodeURIComponent(userQuery)}&depth=${depth}`;
  return services.getAsync<T>(url, { headers: authHeaders(token) });
};



export interface Project {
  project_id: string;
  name: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export const getAllProjects = async <T>(token: string): Promise<T | null> => {
  const url = `${CONSISTENCY_GUARD_URL}/inconsistency-table/projects`;
  return services.getAsync<T>(url, { headers: authHeaders(token) });
};

export const getConsistencyGuardNotesApi = async <T>(
  inconsistencyId: string,
  token: string
): Promise<T | null> => {
  const url = `${CONSISTENCY_GUARD_URL}/inconsistency-notes/inconsistency/${encodeURIComponent(inconsistencyId)}/notes`;
  return services.getAsync<T>(url, { headers: authHeaders(token) });
};

export const saveConsistencyGuardNoteApi = async <T>(
  inconsistencyId: string,
  payload: any,
  token: string
): Promise<T | null> => {
  const url = `${CONSISTENCY_GUARD_URL}/inconsistency-notes/inconsistency/${encodeURIComponent(inconsistencyId)}/note`;
  return services.postAsync<T>(url, payload, { headers: authHeaders(token) });
};

export const getConsistencyGuardDashboardApi = async <T>(
  projectId: string = 'project_1',
  token: string
): Promise<T | null> => {
  const url = `${CONSISTENCY_GUARD_URL}/inconsistency-table/inconsistency/dashboard/${encodeURIComponent(projectId)}`;
  return services.getAsync<T>(url, { headers: authHeaders(token) });
};

export const getConsistencyGuardSummaryApi = async <T>(
  projectId: string = 'project_1',
  token: string
): Promise<T | null> => {
  const url = `${CONSISTENCY_GUARD_URL}/inconsistency-table/inconsistency/summary/${encodeURIComponent(projectId)}`;
  return services.getAsync<T>(url, { headers: authHeaders(token) });
};

export const getConsistencyGuardDetailsApi = async <T>(
  ruleId: string,
  inconsistencyId: string,
  token: string
): Promise<T | null> => {
  const url = `${CONSISTENCY_GUARD_URL}/inconsistency-table/inconsistency/full-details/${encodeURIComponent(ruleId)}/${encodeURIComponent(inconsistencyId)}`;
  return services.getAsync<T>(url, { headers: authHeaders(token) });
};
export const updateConsistencyGuardStatusApi = async <T>(
  inconsistencyId: string,
  payload: any,
  token: string
): Promise<T | null> => {
  const url = `${CONSISTENCY_GUARD_URL}/inconsistency-table/inconsistency/update-status/${encodeURIComponent(inconsistencyId)}`;
  return services.putAsync<T>(url, payload, { headers: authHeaders(token) });
};

export const archiveInconsistencyApi = async <T>(
  inconsistencyId: string,
  token: string): Promise<T | null> => {
  const url = `${CONSISTENCY_GUARD_URL}/inconsistency-table/inconsistency/archive/${encodeURIComponent(inconsistencyId)}`;
  return services.putAsync<T>(url, null, { headers: authHeaders(token) });
}

export const updateConsistencyGuardSeverityApi = async <T>(
  inconsistencyId: string,
  severity: string,
  token: string
): Promise<T | null> => {
  const url = `${CONSISTENCY_GUARD_URL}/inconsistency-table/inconsistency/update-severity/${encodeURIComponent(inconsistencyId)}`;
  return services.putAsync<T>(url, { severity }, { headers: authHeaders(token) });
};


export const exportApprovedInconsistenciesXlsApi = async (
  projectId: string,
  token: string
): Promise<Response> => {
  const url = `${CONSISTENCY_GUARD_URL}/inconsistency-table/export/xls/${encodeURIComponent(projectId)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(token) as HeadersInit,
  });
  if (!response.ok) {
    throw new Error(`Export failed: ${response.statusText}`);
  }
  return response;
};

export interface AssignableReviewer {
  user_id: string;
  nickname: string;
}

export const getAssignableReviewersApi = async <T>(
  token: string
): Promise<T | null> => {
  const url = `${CONSISTENCY_GUARD_URL}/reviewers/get_assignable_reviewers`;
  return services.getAsync<T>(url, { headers: authHeaders(token) });
};

export const assignReviewerApi = async <T>(
  inconsistencyId: string,
  reviewerId: string,
  token: string
): Promise<T | null> => {
  const url = `${CONSISTENCY_GUARD_URL}/inconsistency-table/inconsistency/assign-reviewer/${encodeURIComponent(inconsistencyId)}`;
  return services.putAsync<T>(url, { reviewer_id: reviewerId }, { headers: authHeaders(token) });
};

export async function getChatResponseLocal<T>(payload: ChatData, token: string): Promise<T | null> {
  const url = AI_SERVER.endsWith('/chat_response')
    ? AI_SERVER
    : `${AI_SERVER}/chat_response`;

  console.log("[getChatResponseLocal] URL:", url);
  console.log("[getChatResponseLocal] payload:", JSON.stringify(payload));

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { "Content-Type": "application/json", 'Authorization': `Bearer ${token}`},
      body: JSON.stringify(payload),
    });

    console.log("[getChatResponseLocal] status:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("[getChatResponseLocal] HTTP error:", response.status, errorText);
      throw new Error(`Chat API request failed: ${response.statusText}`);
    }

    const data = await response.json();
    console.log("[getChatResponseLocal] data:", data);
    return data as T;
  } catch (error) {
    console.error("getChatResponseLocal error:", error);
    return null;
  }
}

export interface ChatData {
  user_id: string;
  text: string;
  channel_id: string;
  thread_ts: string;
  ts: string;
  chat_provider_name: string;
}

const ChatApiServices = new ChatApiService();
export default ChatApiServices;
